import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { listenForUtterance, transcribe, speakMessage, stopSpeaking, unlockAudio, openMic, closeMic, getVoiceConfig, createSpeechQueue, chime } from "@/lib/voice";

export function useVoiceEnabled() {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    getVoiceConfig().then((c) => setEnabled(!!c.enabled && !!navigator.mediaDevices?.getUserMedia && !!window.MediaRecorder));
  }, []);
  return enabled;
}

// Hands-free loop: listen → transcribe → send to Nick → read the reply aloud → listen again.
export function useVoiceConversation(send, greet) {
  const [state, setState] = useState("off");
  const [level, setLevel] = useState(0);
  const [heard, setHeard] = useState("");
  const activeRef = useRef(false);
  const sessionRef = useRef(0);
  const stopReqRef = useRef(null);
  const interruptRef = useRef(null);
  const endAfterRef = useRef(false);
  const sendRef = useRef(send);
  sendRef.current = send;
  const greetRef = useRef(greet);
  greetRef.current = greet;

  const loop = useCallback(async () => {
    const id = sessionRef.current;
    const alive = () => activeRef.current && sessionRef.current === id;
    let pending = Promise.resolve();
    while (alive()) {
      setState("listening");
      stopReqRef.current = null;
      let blob;
      try {
        blob = await listenForUtterance({ onLevel: setLevel, shouldStop: () => (alive() ? stopReqRef.current : "cancel") });
      } catch {
        if (alive()) {
          toast.error("Couldn't use the microphone");
          activeRef.current = false;
          closeMic();
          setState("off");
        }
        return;
      }
      if (!alive()) return;
      if (!blob) { closeMic(); setLevel(0); setState("paused"); return; }

      chime();
      setState("transcribing");
      let text = "";
      try { text = await transcribe(blob); } catch { if (alive()) toast.error("Sorry, I didn't catch that — try again"); }
      if (!alive()) return;
      if (!text) continue;
      setHeard(text);

      setState("thinking");
      await pending; // an interrupted reply may still be streaming
      if (!alive()) return;
      const queue = createSpeechQueue({ onStart: () => alive() && setState("speaking") });
      const interrupted = new Promise((resolve) => { interruptRef.current = resolve; });
      const reply = sendRef.current({ message: text, voice: true, onAudio: (ev) => queue.push(ev) });
      const spoken = reply.then(async (done) => {
        if (!alive() || !done?.message_id) return;
        endAfterRef.current = !!done.ticket_form;
        if (queue.count) { await queue.drain(); return; }
        setState("speaking");
        await speakMessage(done.message_id, done.content).catch(() => toast.error("Nick's voice is unavailable — his reply is on screen"));
      });
      await Promise.race([spoken, interrupted]);
      interruptRef.current = null;
      pending = reply;
      if (!alive()) return;
      if (endAfterRef.current) {
        // the support-ticket form needs typing (phone number), so hand back to the keyboard
        endAfterRef.current = false;
        activeRef.current = false;
        closeMic();
        setLevel(0);
        setState("off");
        toast("Add your phone number to the ticket form and hit send");
        return;
      }
    }
  }, []);

  const start = useCallback(async () => {
    unlockAudio();
    try {
      await openMic();
    } catch {
      toast.error("Please allow microphone access to talk to Nick");
      setState("off");
      return;
    }
    sessionRef.current += 1;
    const id = sessionRef.current;
    activeRef.current = true;
    setHeard("");
    const hello = await greetRef.current?.().catch(() => null);
    if (hello?.created && activeRef.current && sessionRef.current === id) {
      setState("speaking");
      try { await speakMessage(hello.message_id, hello.content); } catch { /* greeting is on screen */ }
    }
    if (activeRef.current && sessionRef.current === id) loop();
  }, [loop]);

  const stop = useCallback(() => {
    activeRef.current = false;
    sessionRef.current += 1;
    stopReqRef.current = "cancel";
    stopSpeaking();
    closeMic();
    setLevel(0);
    setState("off");
  }, []);

  // Tap the orb: finish talking now, interrupt Nick, or resume after a pause.
  const tap = useCallback(() => {
    if (state === "listening") stopReqRef.current = "send";
    else if (state === "speaking") { stopSpeaking(); interruptRef.current?.(); }
    else if (state === "paused") { unlockAudio(); sessionRef.current += 1; activeRef.current = true; loop(); }
  }, [state, loop]);

  useEffect(() => () => { activeRef.current = false; stopSpeaking(); closeMic(); }, []);

  return { state, level, heard, start, stop, tap, active: state !== "off" };
}
